import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { useMount } from 'hooks';
import { State } from 'types';
import {
    closeFullScreenStream,
    removeLastPlayedStreamMatchId,
    setLastPlayedStreamMatchId,
    toggleStreamFullscreen,
    unpinStream,
} from '../../actions';
import {
    selectIsFullScreen,
    selectLastPlayedStreamMatchId,
    selectPinnedStreamMatchId,
    selectViewerMode,
} from '../../selectors';
import { InitPlayerFn, IPlayer, ViewerMode } from '../../types';

interface Input {
    matchId: string | null;
}

interface Output {
    initPlayer: InitPlayerFn;
    player: IPlayer | null;
    onTogglePlayStatus: (isPlaying: boolean) => void;
    onStreamUnpin: VoidFn;
    onToggleStreamFullscreen: VoidFn;
    viewerMode: ViewerMode;
}

const selectProps = createStructuredSelector({
    isFullscreenActive: selectIsFullScreen,
    lastPlayedStreamMatchId: selectLastPlayedStreamMatchId,
    pinnedStreamMatchId: selectPinnedStreamMatchId,
    viewerMode: selectViewerMode,
});

const useStream = ({ matchId }: Input): Output => {
    const dispatch = useDispatch();
    const [player, setPlayer] = useState<IPlayer | null>(null);

    const {
        isFullscreenActive,
        lastPlayedStreamMatchId,
        pinnedStreamMatchId,
        viewerMode,
    } = useSelector((s: State) => selectProps(s, {}));

    useMount(() => {
        if (matchId === pinnedStreamMatchId) {
            dispatch(unpinStream());
        }
    });

    const streamMatchIds = useRef<{
        pinnedStreamMatchId: string | null;
        lastPlayedStreamMatchId: string | null;
    }>({
        lastPlayedStreamMatchId,
        pinnedStreamMatchId,
    });

    streamMatchIds.current = {
        lastPlayedStreamMatchId,
        pinnedStreamMatchId,
    };

    // NOTE: re-create this callback only when "matchId" changes, otherwise stream will reload
    // so we need to reference the latest values of "lastPlayedStreamMatchId" and "pinnedStreamMatchId"
    // without passing it to dependency array
    const onTogglePlayStatus = useCallback(
        (isPlaying: boolean) => {
            // used from ref to prevent callback rebuild
            const { current } = streamMatchIds;

            if (!isPlaying) {
                if (matchId === current.lastPlayedStreamMatchId) {
                    dispatch(removeLastPlayedStreamMatchId());
                }

                return;
            }

            if (matchId && matchId !== current.pinnedStreamMatchId) {
                dispatch(setLastPlayedStreamMatchId({ matchId }));
            }
        },
        [dispatch, matchId] // eslint-disable-line
    );

    const onStreamUnpin = useCallback(() => {
        player?.pause();

        dispatch(unpinStream());
    }, [dispatch, player]);

    const onToggleStreamFullscreen = useCallback(() => {
        if (isFullscreenActive) {
            dispatch(closeFullScreenStream());
        } else if (matchId) {
            dispatch(toggleStreamFullscreen({ matchId }));
        }
    }, [dispatch, isFullscreenActive, matchId]);

    return {
        initPlayer: setPlayer,
        onTogglePlayStatus,
        onStreamUnpin,
        onToggleStreamFullscreen,
        viewerMode,
        player,
    };
};

export default useStream;
